/**
 * debtSimplification.js
 * High-precision, integer-cent safe debt simplification and bilateral resolution engine (ES Module).
 */

export function toCents(amount) {
  const num = typeof amount === 'number' ? amount : parseFloat(amount || 0);
  if (isNaN(num)) return 0;
  return Math.round(num * 100);
}

export function fromCents(cents) {
  return Number((cents / 100).toFixed(2));
}

export function calculateNetBalances(transactions, groupMembers = []) {
  const centsBalances = {};

  groupMembers.forEach(userId => {
    centsBalances[userId] = 0;
  });

  (transactions || []).forEach(tx => {
    const hasExplicitSplitPaid = tx.splits && tx.splits.some(s => parseFloat(s.paid_amount || 0) > 0);

    if (hasExplicitSplitPaid) {
      tx.splits.forEach(split => {
        const uid = split.member_user_id;
        if (centsBalances[uid] === undefined) centsBalances[uid] = 0;
        const paidCents = toCents(split.paid_amount || 0);
        const owedCents = toCents(split.owed_amount || 0);
        centsBalances[uid] += paidCents - owedCents;
      });
    } else {
      const payer = tx.paid_by_user_id || tx.user_id;
      const totalCents = toCents(tx.total_amount);

      if (payer) {
        if (centsBalances[payer] === undefined) centsBalances[payer] = 0;
        centsBalances[payer] += totalCents;
      }

      if (Array.isArray(tx.splits)) {
        tx.splits.forEach(split => {
          const debtor = split.member_user_id;
          if (centsBalances[debtor] === undefined) centsBalances[debtor] = 0;
          centsBalances[debtor] -= toCents(split.owed_amount);
        });
      }
    }
  });

  let sumCents = 0;
  let maxUser = null;
  let maxAbs = -1;

  for (const [userId, cents] of Object.entries(centsBalances)) {
    sumCents += cents;
    if (Math.abs(cents) > maxAbs) {
      maxAbs = Math.abs(cents);
      maxUser = userId;
    }
  }

  if (sumCents !== 0 && maxUser) {
    centsBalances[maxUser] -= sumCents;
  }

  const result = {};
  for (const [userId, cents] of Object.entries(centsBalances)) {
    result[userId] = fromCents(cents);
  }

  return result;
}

export function calculatePairwiseDebts(transactions, groupMembers = []) {
  const directMatrix = {};

  const ensureMatrixEntry = (u1, u2) => {
    if (!directMatrix[u1]) directMatrix[u1] = {};
    if (directMatrix[u1][u2] === undefined) directMatrix[u1][u2] = 0;
    if (!directMatrix[u2]) directMatrix[u2] = {};
    if (directMatrix[u2][u1] === undefined) directMatrix[u2][u1] = 0;
  };

  (transactions || []).forEach(tx => {
    const hasExplicitSplitPaid = tx.splits && tx.splits.some(s => parseFloat(s.paid_amount || 0) > 0);

    if (hasExplicitSplitPaid) {
      const payers = [];
      const debtors = [];
      let totalContributed = 0;

      tx.splits.forEach(s => {
        const net = toCents(s.paid_amount || 0) - toCents(s.owed_amount || 0);
        if (net > 0) {
          payers.push({ userId: s.member_user_id, surplus: net });
          totalContributed += net;
        } else if (net < 0) {
          debtors.push({ userId: s.member_user_id, deficit: -net });
        }
      });

      if (totalContributed > 0) {
        debtors.forEach(d => {
          payers.forEach(p => {
            const share = Math.round((d.deficit * p.surplus) / totalContributed);
            if (share > 0 && d.userId !== p.userId) {
              ensureMatrixEntry(d.userId, p.userId);
              directMatrix[d.userId][p.userId] += share;
            }
          });
        });
      }
    } else {
      const payer = tx.paid_by_user_id || tx.user_id;
      if (payer && Array.isArray(tx.splits)) {
        tx.splits.forEach(split => {
          const debtor = split.member_user_id;
          if (debtor && debtor !== payer) {
            const owedCents = toCents(split.owed_amount);
            if (owedCents > 0) {
              ensureMatrixEntry(debtor, payer);
              directMatrix[debtor][payer] += owedCents;
            }
          }
        });
      }
    }
  });

  const pairwiseTransfers = [];
  const processedPairs = new Set();

  const allUserIds = Object.keys(directMatrix);
  for (const u1 of allUserIds) {
    for (const u2 of Object.keys(directMatrix[u1] || {})) {
      if (u1 === u2) continue;
      const pairKey = [u1, u2].sort().join(':::');
      if (processedPairs.has(pairKey)) continue;
      processedPairs.add(pairKey);

      const u1OwesU2 = directMatrix[u1]?.[u2] || 0;
      const u2OwesU1 = directMatrix[u2]?.[u1] || 0;

      const netCents = u1OwesU2 - u2OwesU1;
      if (netCents > 0) {
        pairwiseTransfers.push({
          from: u1,
          to: u2,
          amount: fromCents(netCents)
        });
      } else if (netCents < 0) {
        pairwiseTransfers.push({
          from: u2,
          to: u1,
          amount: fromCents(-netCents)
        });
      }
    }
  }

  pairwiseTransfers.sort((a, b) => b.amount - a.amount);
  return pairwiseTransfers;
}

export function simplifyDebts(memberBalances) {
  const creditors = [];
  const debtors = [];

  for (const [userId, balance] of Object.entries(memberBalances || {})) {
    const cents = toCents(balance);
    if (cents > 0) {
      creditors.push({ userId, balance: cents });
    } else if (cents < 0) {
      debtors.push({ userId, balance: cents });
    }
  }

  let totalCredit = creditors.reduce((s, c) => s + c.balance, 0);
  let totalDebt = debtors.reduce((s, d) => s + (-d.balance), 0);
  const diff = totalCredit - totalDebt;

  if (diff !== 0 && (creditors.length > 0 || debtors.length > 0)) {
    if (diff > 0 && debtors.length > 0) {
      debtors.sort((a, b) => a.balance - b.balance);
      debtors[0].balance -= diff;
    } else if (diff < 0 && creditors.length > 0) {
      creditors.sort((a, b) => b.balance - a.balance);
      creditors[0].balance += -diff;
    }
  }

  const transactions = [];

  while (creditors.length > 0 && debtors.length > 0) {
    debtors.sort((a, b) => a.balance - b.balance);
    creditors.sort((a, b) => b.balance - a.balance);

    const debtor = debtors.shift();
    const creditor = creditors.shift();

    const debtorOwes = -debtor.balance;
    const creditorCredit = creditor.balance;

    const settledCents = Math.min(debtorOwes, creditorCredit);

    if (settledCents > 0) {
      transactions.push({
        from: debtor.userId,
        to: creditor.userId,
        amount: fromCents(settledCents)
      });
    }

    const remainingDebt = debtorOwes - settledCents;
    const remainingCredit = creditorCredit - settledCents;

    if (remainingDebt > 0) {
      debtor.balance = -remainingDebt;
      debtors.push(debtor);
    }

    if (remainingCredit > 0) {
      creditor.balance = remainingCredit;
      creditors.push(creditor);
    }
  }

  transactions.sort((a, b) => b.amount - a.amount);
  return transactions;
}

export function resolveGroupDebts(transactions, groupMembers = [], isSimplified = true) {
  const balances = calculateNetBalances(transactions, groupMembers);
  const pairwiseTransfers = calculatePairwiseDebts(transactions, groupMembers);
  const simplifiedTransfers = simplifyDebts(balances);

  const repaymentsSaved = Math.max(0, pairwiseTransfers.length - simplifiedTransfers.length);
  const transfers = isSimplified ? simplifiedTransfers : pairwiseTransfers;

  return {
    balances,
    netBalances: balances,
    transfers,
    pairwiseTransfers,
    simplifiedTransfers,
    repaymentsSaved
  };
}
